import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ContentCommentRow,
  ContentModerationAction,
} from '@/components/content/content-comment';
import { ContentCommentDetailDialog } from '@/components/content/content-comment-detail-dialog';
import { ContentItemDetailDialog } from '@/components/content/content-item-detail-dialog';
import { RelatedContentLink } from '@/components/content/related-content-link';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FormField } from '@/components/shared/form-field';
import { Pagination } from '@/components/shared/pagination';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api, type Collection } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/utils/format';

const STATUS_OPTIONS = ['published', 'hidden', 'removed'] as const;

const commentStatusVariant = (status: ContentCommentRow['status']): BadgeVariant => {
  if (status === 'published') return 'success';
  if (status === 'hidden') return 'warning';
  return 'danger';
};

const CommentBodyCell = ({ body }: { body: string }) => (
  <span className="line-clamp-2 whitespace-pre-wrap">{body}</span>
);

const CommentStatusBadge = ({ status }: { status: ContentCommentRow['status'] }) => {
  const { t } = useTranslation('content');
  return <Badge variant={commentStatusVariant(status)}>{t(`comments.status.${status}`)}</Badge>;
};

const CommentRowActions = ({
  row,
  onModerate,
}: {
  row: ContentCommentRow;
  onModerate: (row: ContentCommentRow, action: ContentModerationAction) => void;
}) => {
  const { t } = useTranslation('content');
  if (row.status === 'published') {
    return (
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => onModerate(row, 'hide')}>
          {t('comments.moderation.hide')}
        </Button>
        <Button size="sm" variant="destructive" onClick={() => onModerate(row, 'remove')}>
          {t('comments.moderation.remove')}
        </Button>
      </div>
    );
  }
  return (
    <Button size="sm" variant="outline" onClick={() => onModerate(row, 'restore')}>
      {t('comments.moderation.restore')}
    </Button>
  );
};

export const ContentCommentsPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number] | ''>(
    'published',
  );
  const [detail, setDetail] = useState<ContentCommentRow | null>(null);
  const [related, setRelated] = useState<{
    contentType: ContentCommentRow['contentType'];
    contentId: string;
  } | null>(null);
  const [moderateTarget, setModerateTarget] = useState<{
    comment: ContentCommentRow;
    action: ContentModerationAction;
  } | null>(null);

  const dateRange = useContentPeriodStore((state) => state.dateRange);
  const periodKey = `${dateRange.from ?? ''}|${dateRange.to ?? ''}`;
  const [appliedPeriod, setAppliedPeriod] = useState(periodKey);
  if (periodKey !== appliedPeriod) {
    setAppliedPeriod(periodKey);
    setPage(1);
  }

  const list = useQuery({
    queryKey: ['content', 'comments', statusFilter, dateRange.from, dateRange.to, page, pageSize],
    queryFn: () =>
      api.get<Collection<ContentCommentRow>>('/content/comments', {
        query: {
          page,
          pageSize,
          dateFrom: dateRange.from ?? undefined,
          dateTo: dateRange.to ?? undefined,
          ...(statusFilter ? { status: statusFilter } : {}),
        },
      }),
    placeholderData: keepPreviousData,
  });

  const moderate = useMutation({
    mutationFn: (input: { commentId: string; action: ContentModerationAction }) =>
      api.patch<ContentCommentRow>(`/content/comments/${input.commentId}/moderate`, {
        action: input.action,
      }),
    onSuccess: async (updated, variables) => {
      if (variables.action === 'hide') toast.success(t('comments.moderation.hidden'));
      if (variables.action === 'remove') toast.success(t('comments.moderation.removed'));
      if (variables.action === 'restore') toast.success(t('comments.moderation.restored'));
      setModerateTarget(null);
      setDetail((current) =>
        current?.id === updated.id
          ? { ...current, ...updated, contentTitle: current.contentTitle }
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['content', 'comments'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const openRelated = (row: ContentCommentRow) => {
    setDetail(null);
    setRelated({ contentType: row.contentType, contentId: row.contentId });
  };

  const columns: DataTableColumn<ContentCommentRow>[] = [
    {
      id: 'createdAt',
      header: t('comments.columns.createdAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'body',
      header: t('comments.columns.body'),
      cell: (row) => <CommentBodyCell body={row.body} />,
    },
    {
      id: 'author',
      header: t('comments.columns.author'),
      cell: (row) => row.authorName,
    },
    {
      id: 'post',
      header: t('comments.columns.post'),
      cell: (row) => (
        <RelatedContentLink
          contentType={row.contentType}
          contentTitle={row.contentTitle}
          onOpen={() => openRelated(row)}
        />
      ),
    },
    {
      id: 'status',
      header: t('comments.columns.status'),
      cell: (row) => <CommentStatusBadge status={row.status} />,
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const hasFilters = statusFilter !== 'published' || dateRange.preset !== 'allTime';

  return (
    <div>
      <ContentPageHeader />

      <div className="mb-4 max-w-xs">
        <FormField label={t('comments.filterStatus')}>
          {(field) => (
            <Listbox
              id={field.id}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as (typeof STATUS_OPTIONS)[number] | '');
                setPage(1);
              }}
              options={[
                { value: '', label: t('comments.filterAll') },
                ...STATUS_OPTIONS.map((status) => ({
                  value: status,
                  label: t(`comments.status.${status}`),
                })),
              ]}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetail(row)}
        empty={
          list.isError ? (
            <div className="space-y-3 py-4 text-center">
              <p className="text-body font-medium">{tCommon('state.errorTitle')}</p>
              <p className="text-body-sm text-fg-secondary">{tCommon('state.errorBody')}</p>
              <div className="flex justify-end sm:justify-center">
                <Button variant="outline" onClick={() => void list.refetch()}>
                  {tCommon('action.retry')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 py-4 text-center">
              <p className="text-body font-medium">
                {hasFilters ? tCommon('state.noFilterResults') : t('comments.empty.title')}
              </p>
              <p className="text-body-sm text-fg-secondary">
                {hasFilters ? tCommon('state.noFilterResultsBody') : t('comments.empty.description')}
              </p>
            </div>
          )
        }
        rowActions={(row) => (
          <CommentRowActions
            row={row}
            onModerate={(comment, action) => setModerateTarget({ comment, action })}
          />
        )}
        actionsHeader={tCommon('table.actions')}
      />

      {pageMeta && pageMeta.totalItems > 0 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <ContentCommentDetailDialog
        comment={detail}
        open={detail !== null}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
        onModerate={(comment, action) => setModerateTarget({ comment, action })}
        onOpenRelated={openRelated}
      />

      <ContentItemDetailDialog
        contentType={related?.contentType ?? null}
        contentId={related?.contentId ?? null}
        open={related !== null}
        onOpenChange={(open) => {
          if (!open) setRelated(null);
        }}
      />

      <ConfirmDialog
        open={moderateTarget !== null}
        onOpenChange={(open) => {
          if (!open) setModerateTarget(null);
        }}
        title={
          moderateTarget
            ? t(`comments.moderation.${moderateTarget.action}Confirm.title`)
            : ''
        }
        description={
          moderateTarget
            ? t(`comments.moderation.${moderateTarget.action}Confirm.body`)
            : ''
        }
        confirmLabel={
          moderateTarget ? t(`comments.moderation.${moderateTarget.action}`) : tCommon('action.cancel')
        }
        cancelLabel={tCommon('action.cancel')}
        destructive={moderateTarget?.action === 'remove'}
        onConfirm={() => {
          if (!moderateTarget) return;
          moderate.mutate({
            commentId: moderateTarget.comment.id,
            action: moderateTarget.action,
          });
        }}
        loading={moderate.isPending}
      />
    </div>
  );
};
